import AddMemberDialog from "@components/organization/AddMemberDialog";
import DeleteMemberDialog from "@components/organization/DeleteMemberDialog";
import EditMemberDialog from "@components/organization/EditMemberDialog";
import { useUserContext } from "@contexts/UserContext/index.context";
import { PersonAdd as PersonAddIcon } from "@mui/icons-material";
import { Alert, Box, Button, Chip, CircularProgress, Container, Stack } from "@mui/material";
import type { PaginationState, SortingState } from "@tanstack/react-table";
import { UserRank } from "@types_def/models/user.type";
import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { DataTable } from "@/components/ui/data-table";
import { DataTableToolbar } from "@/components/ui/data-table-toolbar";
import { useDataTable } from "@/hooks/use-data-table";
import { useMembersQuery } from "@/libs/generated/hooks";
import type { MembersQuery } from "@/libs/generated/operations";

import { MEMBERS_TABLE_COLUMNS } from "./columns";

type MemberRecord = MembersQuery["members"]["records"][0];

type ActionHandlers = {
  onEdit?: (member: MemberRecord) => void;
  onDelete?: (member: MemberRecord) => void;
};

type MemberRecordWithActions = MemberRecord & { _actions?: ActionHandlers };

type OrganizationMembersTableProps = {
  memberRank: UserRank;
};

function OrganizationMembersTable({ memberRank }: OrganizationMembersTableProps) {
  "use no memo";
  const { pathname } = useLocation();
  const isManagersPage = pathname.includes("/view-managers");

  const { t } = useTranslation();
  const { userState } = useUserContext();
  const { slug, organizationName } = useParams<{ slug: string; organizationName: string }>();

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [addMemberDialogOpen, setAddMemberDialogOpen] = React.useState(false);
  const [editMemberDialogOpen, setEditMemberDialogOpen] = React.useState(false);
  const [deleteMemberDialogOpen, setDeleteMemberDialogOpen] = React.useState(false);
  const [selectedMember, setSelectedMember] = React.useState<MemberRecord | null>(null);

  const isAdmin = userState?.["custom:role"] === "ADMIN";
  const isManager = memberRank === UserRank.ORGANIZATION_MANAGER;

  const handleEdit = React.useCallback((member: MemberRecord) => {
    setSelectedMember(member);
    setEditMemberDialogOpen(true);
  }, []);

  const handleDelete = React.useCallback((member: MemberRecord) => {
    setSelectedMember(member);
    setDeleteMemberDialogOpen(true);
  }, []);

  const actions = React.useMemo(
    () => ({ onEdit: handleEdit, onDelete: handleDelete }),
    [handleEdit, handleDelete]
  );

  const {
    data,
    isPending: loading,
    error,
  } = useMembersQuery({
    GetMembersByRankInput: {
      page: pagination.pageIndex,
      take: pagination.pageSize,
      rank: memberRank,
      ...(isAdmin && slug !== undefined && slug !== null ? { slug } : {}),
    },
  });

  const tableData: MemberRecordWithActions[] = (data?.members?.records ?? []).map((record) => ({
    ...record,
    _actions: actions,
  }));

  const totalCount = data?.members?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / pagination.pageSize));

  const { table } = useDataTable<MemberRecordWithActions>({
    data: tableData,
    columns: MEMBERS_TABLE_COLUMNS,
    pageCount,
    manualPagination: true,
    externalPagination: pagination,
    externalSorting: sorting,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
  });

  if (loading) {
    return (
      <Box alignItems="center" display="flex" justifyContent="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error !== null && error !== undefined) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error.message}
      </Alert>
    );
  }

  return (
    <Container maxWidth={false}>
      <Stack spacing={2}>
        <DataTable isLoading={loading} table={table} totalCount={totalCount}>
          <DataTableToolbar table={table}>
            <Box sx={{ flex: 1 }} />
            {(isAdmin || isManager) && (
              <Button
                startIcon={<PersonAddIcon />}
                onClick={() => setAddMemberDialogOpen(true)}
                variant="contained"
              >
                {t("common.actions.create")}{" "}
                {isManagersPage
                  ? t("layouts.sub-headers.organization.managers")
                  : t("layouts.sub-headers.organization.members")}
              </Button>
            )}
          </DataTableToolbar>
        </DataTable>
        {isManagersPage && (
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip label={`${totalCount} ${t("common.shared.total")}`} />
          </Stack>
        )}
      </Stack>
      {organizationName !== undefined && (
        <>
          <AddMemberDialog
            memberRank={memberRank}
            open={addMemberDialogOpen}
            onClose={() => setAddMemberDialogOpen(false)}
            organizationName={organizationName}
            organizationSlug={slug ?? ""}
          />
          <EditMemberDialog
            member={selectedMember}
            open={editMemberDialogOpen}
            onClose={() => setEditMemberDialogOpen(false)}
          />
          <DeleteMemberDialog
            member={selectedMember}
            open={deleteMemberDialogOpen}
            onClose={() => setDeleteMemberDialogOpen(false)}
          />
        </>
      )}
    </Container>
  );
}

export default OrganizationMembersTable;
